import type { FlowMove, FlowWindow, FlowWindowStat } from "../../shared/api.js";
import type { Stack } from "../../shared/stack.js";
import { h } from "../dom.js";
import { amount, compact, duration, full, time } from "../format.js";

export const RATE_UNITS = { s: 1_000, m: 60_000, h: 3_600_000, d: 86_400_000 } as const;
export type RateUnit = keyof typeof RATE_UNITS;

const WINDOW_LABEL: Record<FlowWindow, string> = { "1h": "Last hour", "24h": "Last 24 h", "7d": "Last 7 days", "30d": "Last 30 days", "1y": "Last year" };
const TYPE_UNIT: Record<Stack["type"], string> = { item: "", fluid: " mB", essentia: "" };

export function windowLabel(w: FlowWindow): string {
  return WINDOW_LABEL[w];
}

/** A total over `observedMs` as a rate per unit, "+1.25/s", "−3.4k/h". Null when nothing was watched. */
export function rate(total: number, observedMs: number, unit: RateUnit, type: Stack["type"], signed = false): string {
  if (observedMs <= 0) return "–";
  const n = (total / observedMs) * RATE_UNITS[unit];
  const abs = Math.abs(n);
  const sign = signed && n > 0 ? "+" : n < 0 ? "−" : "";
  let text: string;
  if (abs === 0) text = "0";
  else if (abs < 0.01) text = abs.toExponential(1);
  else if (abs < 10) text = abs.toFixed(2).replace(/\.?0+$/, "");
  else if (abs < 1000) text = abs.toFixed(1).replace(/\.0$/, "");
  else text = compact(abs);
  return `${sign}${text}${TYPE_UNIT[type]}/${unit}`;
}

function loadUnit(): RateUnit {
  try {
    const v = localStorage.getItem("flow-unit");
    if (v && Object.hasOwn(RATE_UNITS, v)) return v as RateUnit;
  } catch {
    // ignore
  }
  return "h";
}

/** The /s /m /h /d switch, shared by every flow table and remembered in this browser. */
export class UnitToggle {
  unit: RateUnit = loadUnit();
  readonly el: HTMLElement;

  constructor(onChange: () => void) {
    this.el = h("div", { class: "segmented", role: "group", "aria-label": "Rate unit" });
    for (const u of Object.keys(RATE_UNITS) as RateUnit[]) {
      this.el.append(
        h(
          "button",
          {
            type: "button",
            class: "small",
            "data-unit": u,
            title: `Per ${{ s: "second", m: "minute", h: "hour", d: "day" }[u]}`,
            onclick: () => {
              this.unit = u;
              try {
                localStorage.setItem("flow-unit", u);
              } catch {
                // ignore
              }
              this.sync();
              onChange();
            },
          },
          `/${u}`,
        ),
      );
    }
    this.sync();
  }

  private sync(): void {
    for (const b of this.el.querySelectorAll("button")) b.classList.toggle("active", b.dataset.unit === this.unit);
  }
}

/** One row per window: totals in and out, and their averages per the chosen unit. */
export function windowsTable(windows: FlowWindowStat[], stack: Stack, unit: RateUnit): HTMLElement {
  const t = stack.type;
  return h(
    "div",
    { class: "table-wrap" },
    h(
      "table",
      { class: "compact flows" },
      h(
        "thead",
        null,
        h(
          "tr",
          null,
          h("th", null, "Window"),
          h("th", { class: "num" }, "In"),
          h("th", { class: "num" }, "Out"),
          h("th", { class: "num" }, `In /${unit}`),
          h("th", { class: "num" }, `Out /${unit}`),
          h("th", { class: "num" }, `Net /${unit}`),
          h("th", { class: "num" }, "Changes"),
          h("th", { class: "num", title: "Time the app watched it live in this window; the averages divide by this" }, "Watched"),
        ),
      ),
      h(
        "tbody",
        null,
        ...windows.map((w) =>
          h(
            "tr",
            { title: w.exact ? "" : "From hourly totals: older single changes are no longer kept, so this window starts on the hour" },
            h("td", null, windowLabel(w.window), w.exact ? null : h("span", { class: "muted" }, " ≈")),
            h("td", { class: "num", title: full(w.in) }, amount(w.in, t)),
            h("td", { class: "num", title: full(w.out) }, amount(w.out, t)),
            h("td", { class: "num" }, rate(w.in, w.observedMs, unit, t)),
            h("td", { class: "num" }, rate(w.out, w.observedMs, unit, t)),
            h("td", { class: "num" }, rate(w.in - w.out, w.observedMs, unit, t, true)),
            h("td", { class: "num" }, full(w.moves)),
            h("td", { class: "num" }, w.observedMs ? duration(w.observedMs) : "–"),
          ),
        ),
      ),
    ),
  );
}

/** Every change, newest first. */
export function movesTable(moves: FlowMove[], stack: Stack): HTMLElement {
  return h(
    "table",
    { class: "compact" },
    h("thead", null, h("tr", null, h("th", null, "When"), h("th", { class: "num" }, "Change"), h("th", { class: "num" }, "Amount after"))),
    h(
      "tbody",
      null,
      ...moves.map((m) =>
        h(
          "tr",
          null,
          h("td", null, time(m.at)),
          h("td", { class: `num ${m.delta > 0 ? "flow-in" : "flow-out"}`, title: full(m.delta) }, `${m.delta > 0 ? "+" : "−"}${amount(Math.abs(m.delta), stack.type)}`),
          h("td", { class: "num", title: full(m.amount) }, amount(m.amount, stack.type)),
        ),
      ),
    ),
  );
}
